<script setup>
import { defineProps, ref } from 'vue';
import MsPopover from '@/components/popover/MsPopover.vue';
import { MsPopoverPosition } from '@/lib/vueFinalPopover/VueFinalPopover.vue';

const props = defineProps({
  /**
   * Có hiển thị trên 1 dòng không
   */
  isDisplayInline: {
    type: Boolean,
    default: false
  },

  /**
   * Vị trí xuất hiện.
   * x: vị trí đặt popover vào trục x của target element left, center, right
   * y: vị trí đặt popover vào trục y của target element top, center, bottom
   * offsetX: margin-left của popover so với trục x
   * offsetY: margin-top của popover so với trục y
   * isOuterX: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục X (Lấy trục Y làm tâm) ngược lại hướng vào trong
   * isOuterY: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục Y (Lấy trục X làm tâm) ngược lại hướng vào trong
   */
  position: {
    type: Object,
    default: () => ({
      x: MsPopoverPosition.Left,
      y: MsPopoverPosition.Bottom,
      offsetX: 0,
      offsetY: 0,
      isOuterX: false,
      isOuterY: true,
    }),
    validator(val) {
      // offsetX chỉ cho phép số
      if (val.offsetX !== undefined && (typeof val.offsetX !== 'number')) {
        console.warn('offsetX chỉ cho phép số');
        return false;
      }
      // offsetY chỉ cho phép số
      if (val.offsetY !== undefined && (typeof val.offsetY !== 'number')) {
        console.warn('offsetY chỉ cho phép số');
        return false;
      }
      // Trục X chỉ nhận giá trị left, center, right
      if (
        val.x !== undefined &&
        val.x !== MsPopoverPosition.Left && 
        val.x !== MsPopoverPosition.Right &&
        val.x !== MsPopoverPosition.Center
      ) {
        console.warn('Trục X chỉ nhận giá trị left, center, right');
        return false;
      }
      // Trục Y chỉ nhận giá trị left, center, right
      if (
        val.y !== undefined &&
        val.y !== MsPopoverPosition.Top &&
        val.y !== MsPopoverPosition.Bottom &&
        val.y !== MsPopoverPosition.Center
      ) {
        console.warn('Trục Y chỉ nhận giá trị top, center, bottom');
        return false;
      }
      // isOuterX chỉ nhận kiểu boolean
      if (val.isOuterX !== undefined && (typeof val.isOuterX !== 'boolean')) {
        console.warn('isOuterX chỉ nhận kiểu boolean');
        return false;
      }
      // isOuterY chỉ nhận kiểu boolean
      if (val.isOuterY !== undefined && (typeof val.isOuterY !== 'boolean')) {
        console.warn('isOuterY chỉ nhận kiểu boolean');
        return false;
      }

      return true;
    },
  },
  /**
   * Chiều rộng của popover
   */
  width: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao của popover
   */
  height: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều rộng tối thiểu của popover
   */
  minWidth: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao tối thiểu của popover
   */
  minHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều rộng tối đa của popover
   */
  maxWidth: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao tối đa của popover
   */
  maxHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Transition cho popover
   */
  transition: {
    type: String,
    default: 'ms-fade',
  },
  /**
   * z-index
   */
  zIndex: {
    type: Number,
    default: 10,
  },
  /**
   * Có mũi tên hay ko
   */
  hasArrow: {
    type: Boolean,
    default: false,
  },
  /**
   * Custom style của mũi tên
   */
  customStyleArrow: {
    type: Object,
    default: () => ({}),
  },
  /**
   * custom style
   */
  customStyle: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Có đóng khi hover vào popover ko
   */
  isCloseWhenHoverPopover: {
    type: Boolean,
    default: false,
  },
});

/**
 * Ref content
 */
const contentRef = ref(null);

/**
 * Ref popover
 */
const popoverRef = ref(null);

/**
 * Có active ko
 */
const isActive = ref(false);

/**
 * Hiện popover khi di chuột vào
 * @author: BMThang(06/12/2022)
 */
const handleMouseEnter = () => {
  isActive.value = true;
};

/**
 * Đóng popover khi di chuột ra khỏi content và popover
 * @param {object} e event object
 * @author: BMThang(06/12/2022)
 */
const handleMouseLeave = (e) => {
  if (
    !props.isCloseWhenHoverPopover &&
    popoverRef.value?.popoverRef?.contains(e.toElement)
  ) {
    return;
  }

  isActive.value = false;
};

</script>

<template>
  <!-- Element chính -->
  <div 
    ref="contentRef"
    class="custom"
    :class="{'display-inline': isDisplayInline}"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot />
  </div>

  <!-- Element trong tooltip -->
  <ms-popover
    ref="popoverRef"
    :target="contentRef"
    :is-active="isActive"
    :position="position"
    :has-arrow="hasArrow"
    :custom-style-arrow="customStyleArrow"
    :custom-style="customStyle"
    :z-index="zIndex"
    :transition="transition"
    :width="width"
    :height="height"
    :max-width="maxWidth"
    :max-height="maxHeight"
    :min-width="minWidth"
    :min-height="minHeight"
    :is-tooltip="true"
    @onMouseLeave="handleMouseLeave"
  >
    <slot name="content" />
  </ms-popover>
</template>

<style lang="scss" scoped>
.display-inline {
  display: inline;
}
</style>